import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormElement } from '../FormElement';
import { InputText } from '../Inputs/InputText';
import {
  SubmitHandler,
  useForm,
  FormProvider,
  UseFormReturn,
} from 'react-hook-form';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { FormSection } from '../FormSection';
import { FormSubmitActions } from '../FormSubmitActions';
import { StatusInfo } from '../Organizations/StatusInfo';
import { OrganizationDTO } from '@/api/trafikportalen/models/organizationDTO';
import {
  UpdateOrganizationByIDMutationBody,
  useUpdateOrganizationByID,
  getGetOrganizationByIDQueryKey,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { InputNumber } from '../Inputs/InputNumber';
import { useQueryClient } from '@tanstack/react-query';
import {
  EINVOICE_EMAIL_MAX_LENGTH,
  einvoiceEmailFieldError,
  glnFieldError,
  normalizeGlnInput,
} from '@/helpers/invoicingIdentifiers';
import { blankToUndefined, trimData } from '@/helpers/trimData';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import CheckBox from '../core/Checkbox';
import Input from '../core/Input';
import { formatDate } from '@/helpers/formatting';
import { getOrganizationEffectiveState } from '@/helpers/organizationStatus';
import { COLLECTION_TYPE_LABELS } from '@/helpers/collectionTypes';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';

interface OrganizationFormProps {
  organization: OrganizationDTO;
}

export const OrganizationDetailsForm = ({
  organization,
}: OrganizationFormProps) => {
  const { formatMessage } = useIntl();
  const updateOrganization = useUpdateOrganizationByID();
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const queryClient = useQueryClient();
  const studentsDisabled = useStudentsDisabled();
  const { disableDiscountCodes } = useTenantConfiguration();

  const [editMode, setEditMode] = useState(false);

  const methods = useForm<UpdateOrganizationByIDMutationBody>({
    defaultValues: {
      ...organization,
      credit: organization.credit === undefined ? -1 : organization.credit,
    },
    mode: 'onBlur',
  });

  const hasNoCreditLimit = methods.watch('credit') === -1;

  const effectiveState = getOrganizationEffectiveState(organization);

  const startEdit = useCallback(() => setEditMode(true), []);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
    methods.reset();
  }, [methods]);

  const onSubmit: SubmitHandler<UpdateOrganizationByIDMutationBody> =
    useCallback(
      (data) => {
        const trimmedData = trimData(data);
        trimmedData.gln = blankToUndefined(trimmedData.gln);
        trimmedData.einvoice_email = blankToUndefined(
          trimmedData.einvoice_email,
        );

        if (trimmedData.credit && trimmedData.credit < 0) {
          delete trimmedData.credit;
        }

        const master = trimmedData.discount_codes?.master;
        if (master && !master.full_product) {
          delete trimmedData.discount_codes.master;
        }

        updateOrganization.mutate(
          {
            organizationID: organization.id,
            data: trimmedData,
          },
          {
            onSuccess: async () => {
              toast.success(
                {
                  title: 'Organisation uppdaterad',
                },
                { autoClose: 3000 },
              );

              await queryClient.invalidateQueries({
                queryKey: getGetOrganizationByIDQueryKey(organization.id),
              });
              setEditMode(false);
            },
            onError: (error) => {
              console.error(error);
              toast.error(
                {
                  title: 'Kunde inte uppdatera organisationen',
                },
                { autoClose: 3000 },
              );
            },
          },
        );
      },
      [organization.id, queryClient, updateOrganization],
    );

  if (!organization)
    return (
      <div>
        <FormattedMessage defaultMessage="Ingen organisation hittad" />
      </div>
    );

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-4"
      >
        <FormSection
          title={<FormattedMessage defaultMessage="Allmän information" />}
          onClickButton={startEdit}
          editMode={editMode}
          canSeeButton={checkUserPermissions({
            permission: 'tenant:editor',
          })}
        >
          <div className="flex flex-col gap-2">
            <FormElement
              id="name"
              label={<FormattedMessage defaultMessage="Namn" />}
              input={
                <InputText
                  name="name"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.name}
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Organisations­nummer" />}
              input={<p>{organization.organization_number}</p>}
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Status" />}
              input={<StatusInfo status={effectiveState} />}
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Skapad" />}
              input={<p>{formatDate(organization.created_at)}</p>}
            />
            {!studentsDisabled && (
              <FormElement
                label={<FormattedMessage defaultMessage="Elevkategorier" />}
                input={
                  <p>
                    {organization.enabled_collection_types?.length > 0
                      ? organization.enabled_collection_types
                          .map((t) => formatMessage(COLLECTION_TYPE_LABELS[t]))
                          .join(', ')
                      : formatMessage({ defaultMessage: 'Inga' })}
                  </p>
                }
              />
            )}
          </div>
        </FormSection>
        <FormSection
          title={<FormattedMessage defaultMessage="Kredit" />}
          editMode={editMode}
        >
          <CreditInput
            editMode={editMode}
            hasNoCreditLimit={hasNoCreditLimit}
            methods={methods}
            organization={organization}
          />
          <p className="text-text-800">
            {studentsDisabled ? (
              <FormattedMessage defaultMessage="Hur mycket organisationen kan spendera som mest under en kalendermånad. Inkluderar både biljetter köpta i portalen och tjänsteresor." />
            ) : (
              <FormattedMessage defaultMessage="Hur mycket organisationen kan spendera som mest under en kalendermånad. Inkluderar både biljetter köpta i portalen och tjänsteresor, men inte skolbiljetter. Elevkategorierna har ingen kreditgräns." />
            )}
          </p>
          <p className="text-text-800">
            <FormattedMessage defaultMessage="Om gränsen sänks till lägre än vad som redan är låst i tjänsteresor kommer de resenärerna ändå kunna fortsätta spendera upp till sina kreditgränser. Det kommer dock inte vara möjligt att tilldela mer kredit." />
          </p>
        </FormSection>
        <FormSection
          title={<FormattedMessage defaultMessage="E-faktura" />}
          description={
            <FormattedMessage defaultMessage="Nedan fält används inte vid fakturering utan är bara till för er själva ha koll på." />
          }
          editMode={editMode}
        >
          <div className="flex flex-col gap-2">
            <FormElement
              id="gln"
              label={<FormattedMessage defaultMessage="GLN-nummer" />}
              input={
                <InputText
                  name="gln"
                  type="number"
                  normalize={normalizeGlnInput}
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.gln}
                  noValueDescription={
                    <FormattedMessage defaultMessage="Inget GLN-nummer angivet" />
                  }
                  rules={{
                    validate: (value) => glnFieldError(value, formatMessage),
                  }}
                />
              }
            />
            <FormElement
              id="einvoice_email"
              label={<FormattedMessage defaultMessage="E-fakturaadress" />}
              input={
                <InputText
                  name="einvoice_email"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.einvoice_email}
                  placeholder="faktura@exempel.se"
                  noValueDescription={
                    <FormattedMessage defaultMessage="Ingen e-fakturaadress angiven" />
                  }
                  maxLength={EINVOICE_EMAIL_MAX_LENGTH}
                  rules={{
                    validate: (value) =>
                      einvoiceEmailFieldError(value, formatMessage),
                  }}
                />
              }
            />
          </div>
        </FormSection>

        {!disableDiscountCodes && (
          <FormSection
            title={<FormattedMessage defaultMessage="Rabattkod" />}
            description={
              <FormattedMessage defaultMessage="Rabattkoden kommer att appliceras på samtliga manuellt gjorda köp i företagsdelen av portalen, men inte för tjänsteresor i app eller på kort." />
            }
            editMode={editMode}
          >
            <InputText
              name="discount_codes.master.full_product"
              noValueDescription={
                <FormattedMessage defaultMessage="Ingen rabattkod angiven" />
              }
              control={methods.control}
              editMode={editMode}
            />
          </FormSection>
        )}

        {editMode && (
          <FormSubmitActions
            active={true}
            createMode={false}
            onCancel={cancelEdit}
            isLoading={updateOrganization.isPending}
          />
        )}
      </form>
    </FormProvider>
  );
};

const CreditInput = ({
  editMode,
  hasNoCreditLimit,
  methods,
  organization,
}: {
  editMode: boolean;
  hasNoCreditLimit: boolean;
  methods: UseFormReturn<UpdateOrganizationByIDMutationBody>;
  organization: OrganizationDTO;
}) => {
  const { formatMessage } = useIntl();

  const [creditString, setCreditString] = useState(
    organization.credit?.toString() || '',
  );

  const content = useMemo(() => {
    if (editMode) {
      return (
        <div className="flex w-full items-center justify-end gap-5">
          {!hasNoCreditLimit && (
            <Input
              name="credit"
              value={creditString}
              className="max-w-32"
              onChange={(e) => {
                let value = e.target.value;
                if (value !== '0') {
                  value = value.replace(/^0+/, '');
                }
                setCreditString(value);
                const creditValue = parseInt(value);
                if (!isNaN(creditValue)) {
                  methods.setValue(
                    'credit',
                    creditValue >= 0 ? creditValue : 0,
                    { shouldDirty: true },
                  );
                  methods.trigger();
                }
              }}
              onBlur={() =>
                setCreditString(methods.getValues('credit')?.toString() || '')
              }
            />
          )}
          <CheckBox
            id="has_no_credit_limit"
            checked={hasNoCreditLimit}
            onClick={() => {
              if (hasNoCreditLimit) {
                methods.setValue('credit', organization.credit || 0, {
                  shouldDirty: true,
                });
              } else {
                methods.setValue('credit', -1, {
                  shouldDirty: true,
                });
              }
              methods.trigger();
            }}
            label={formatMessage({
              defaultMessage: 'Ingen kreditgräns',
            })}
          />
        </div>
      );
    }

    if (hasNoCreditLimit) {
      return (
        <p className="ml-auto">
          <FormattedMessage defaultMessage="Ingen kreditgräns" />
        </p>
      );
    }

    return (
      <InputNumber name="credit" control={methods.control} editMode={false} />
    );
  }, [
    creditString,
    editMode,
    formatMessage,
    hasNoCreditLimit,
    methods,
    organization.credit,
  ]);

  return (
    <FormElement
      label={<FormattedMessage defaultMessage="Kreditgräns (SEK)" />}
      input={content}
    />
  );
};
